import { useElementWidth } from 'lib/use-element-size'
import React, { FC, useRef, useState } from 'react'
import { useMouseHovered } from 'react-use'
import styled from 'styled-components'

import { createPluralFormWithPlaceholder } from '../../lib/pluralize'
import { FillerBackdrop } from '../filler'
import { Image, Props as ImageProps } from '../image'
import { Dots } from './Dots'
import { Slide } from './Slide'
import { SliderContent } from './SliderContent'

export type Props = {
  slides: Array<ImageProps>
  maxSlides: number
  i18n: { moreSlidesForms: [string, string, string] }
  showPhotosOverflow?: boolean
}

export const HoverGallery: FC<Props> = ({ slides, maxSlides, i18n, showPhotosOverflow, children, ...restProps }) => {
  const [interactive, setInteractive] = useState(false)

  const visibleSlidesLength = Math.min(slides.length, maxSlides)

  const wrapperRef = useRef<HTMLInputElement>(null)

  const width = useElementWidth(wrapperRef)

  const { elX } = useMouseHovered(wrapperRef, { bound: true, whenHovered: true })

  const pixelsPerSlide = width === null ? 0 : width / (visibleSlidesLength - 1)

  const activeSlide = interactive ? Math.round(elX / pixelsPerSlide) : 0

  const visibleSlides = slides.slice(0, maxSlides)

  return (
    <StyledSlider
      {...restProps}
      ref={wrapperRef}
      onMouseOver={() => setInteractive(true)}
      onMouseOut={() => setInteractive(false)}
    >
      <SliderContent activeSlide={activeSlide}>
        {visibleSlides.map((image, i) => (
          <Slide isActive={activeSlide === i} key={i}>
            <Image {...image} />
            {i === visibleSlides.length - 1 && showPhotosOverflow && visibleSlides.length < slides.length && (
              <FillerBackdrop>
                <MorePhotos>
                  {createPluralFormWithPlaceholder(i18n.moreSlidesForms, slides.length - visibleSlidesLength)}
                </MorePhotos>
              </FillerBackdrop>
            )}
          </Slide>
        ))}
      </SliderContent>
      {visibleSlidesLength > 1 && (
        <Dots slidesCount={visibleSlidesLength} activeSlide={activeSlide} visible={interactive} />
      )}
    </StyledSlider>
  )
}

const MorePhotos = styled.div`
  ${(props) => props.theme.typography.paragraph.p1}
`

const StyledSlider = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`
